import {Localized, useLocalization} from "@fluent/react";
import {
	Autocomplete,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	ListSubheader,
	TextField,
	autocompleteClasses,
} from "@mui/material";
import {makeStyles} from "@mui/styles";
import type {Modifier} from "@popperjs/core";
import clsx from "clsx";
import React from "react";

import type Organisation from "../../store/organisation/Organisation";

const searchEnableThreshold = 5;

function OrganisationSelector(props: {
	currentOrganisation: Organisation | null;
	organisations: Organisation[];
	showCurrentIfUserNotInOrganisation?: boolean;
	onClose: () => void;
}) {
	const {currentOrganisation, organisations} = props;

	const showUserNotInOrg =
		Boolean(props.showCurrentIfUserNotInOrganisation && currentOrganisation) &&
		organisations.every((o) => o.name !== currentOrganisation?.name);

	return (
		<>
			{showUserNotInOrg && currentOrganisation && (
				<HeaderForExternalUser
					organisation={currentOrganisation}
					onSelect={props.onClose}
				/>
			)}

			{organisations.length > searchEnableThreshold ? (
				<OrganisationListWithSearch
					current={currentOrganisation}
					organisations={organisations}
					noTopPadding={showUserNotInOrg}
					onSelect={props.onClose}
				/>
			) : (
				<OrganisationList
					current={currentOrganisation}
					organisations={organisations}
					noTopPadding={showUserNotInOrg}
					onSelect={props.onClose}
				/>
			)}
		</>
	);
}

function OrganisationList(props: {
	current: Organisation | null;
	organisations: Organisation[];
	noTopPadding: boolean;
	onSelect: () => void;
}) {
	const {current, organisations} = props;

	return (
		<List sx={[props.noTopPadding && {pt: 0}]}>
			{organisations.map((o, i) => (
				<OrganisationListItem
					key={i}
					current={current?.name === o.name}
					organisation={o}
					onSelect={props.onSelect}
				/>
			))}
		</List>
	);
}

const useSearchListStyles = makeStyles((theme) => ({
	inputBase: {
		padding: theme.spacing(0, 2),
	},
	paper: {
		margin: 0,
		boxShadow: "none",
	},
	root: {
		borderBottom: "1px solid",
		borderBottomColor: theme.palette.divider,
		paddingBottom: theme.spacing(2),
	},
	paddingTop: {
		paddingTop: theme.spacing(2),
	},
	listbox: {
		[`& .${autocompleteClasses.option}`]: {
			padding: 0,
		},
		maxHeight: "unset",
	},
}));

function OrganisationListWithSearch(props: {
	organisations: Organisation[];
	current: Organisation | null;
	noTopPadding: boolean;
	onSelect: () => void;
}) {
	const {current, organisations} = props;

	const classes = useSearchListStyles();

	const {l10n} = useLocalization();

	return (
		<Autocomplete
			classes={{
				paper: classes.paper,
				root: clsx(classes.root, {[classes.paddingTop]: !props.noTopPadding}),
				listbox: classes.listbox,
			}}
			open
			clearOnBlur={false}
			disablePortal
			options={organisations}
			onKeyDown={(event) => {
				if (event.key === "Escape") {
					props.onSelect();
				}
			}}
			renderInput={(params) => (
				<TextField
					ref={params.InputProps.ref}
					slotProps={{htmlInput: params.inputProps}}
					autoFocus
					fullWidth
					className={classes.inputBase}
					placeholder={l10n.getString(
						"organisation-selector-search-placeholder",
						null,
						"Search"
					)}
				/>
			)}
			slotProps={{
				popper: {
					modifiers: [relativePosition],
				},
			}}
			ListboxProps={{tabIndex: -1}}
			getOptionLabel={(org) => org.displayName}
			getOptionKey={(org) => org.name}
			renderOption={(prop, org) => (
				<OrganisationListItem
					key={prop.key}
					current={current?.name === org.name}
					organisation={org}
					optionProps={prop}
					onSelect={props.onSelect}
				/>
			)}
			renderTags={() => null}
			onChange={(_, o) => {
				if (o && (!current || o.name !== current.name)) {
					window.location.href = `/organisations/${o.name}`;
				}
			}}
		/>
	);
}

const relativePosition: Modifier<"applyStyles", object> = {
	name: "applyStyles",
	enabled: true,
	phase: "write",
	fn({state}) {
		Object.assign(state.elements.popper.style, {
			position: "relative",
			overflow: "auto",
		});
	},
};

function OrganisationListItem(props: {
	organisation: Organisation;
	current: boolean;
	optionProps?: React.HTMLAttributes<HTMLLIElement>;
	onSelect: () => void;
}) {
	const {current, organisation: o, onSelect} = props;

	return (
		<ListItem disablePadding {...props.optionProps}>
			<ListItemButton
				selected={current}
				component="a"
				href={`/organisations/${o.name}`}
				onClick={(event: React.MouseEvent) => {
					event.stopPropagation();
					onSelect();
				}}
			>
				<ListItemText primary={o.displayName} />
			</ListItemButton>
		</ListItem>
	);
}

function HeaderForExternalUser(props: {
	organisation: Organisation;
	onSelect: () => void;
}) {
	const {organisation} = props;

	return (
		<>
			<ListSubheader disableSticky component="div">
				<Localized id="organisation-selector-current-org-label">
					Current organisation
				</Localized>
			</ListSubheader>

			<OrganisationListItem
				current
				organisation={organisation}
				onSelect={props.onSelect}
			/>

			<ListSubheader disableSticky component="div">
				<Localized id="organisation-selector-user-orgs-label">
					Your organisations
				</Localized>
			</ListSubheader>
		</>
	);
}

export default OrganisationSelector;
